import React from 'react';

import GenericChart, { chartColors, DEFAULT_HEIGHT, exportConfig, fontFamily } from '~/GenericChart/GenericChart';
import { roundedHumanFormat } from '~/utils/Price/Price';
import { SeriesOptionsType, SVGElement } from 'highcharts';
import { ERPApiResult } from '~/libs/infrastructure/schemas/erp';

const THOUSANDS_ROUNDING_THRESHOLD = 900;

interface ChartQuadrantProps {
	currency: string;
	parseErpResults: ERPApiResult;
}

export function ChartQuadrant({ currency, parseErpResults }: ChartQuadrantProps) {
	const renderedSvgRef = React.useRef<null | SVGElement>(null);

	let highestXValue = 0;
	const series: SeriesOptionsType[] = parseErpResults.stats.quadrants.map((stat) => {
		const currentXValue = Number(stat.packPrice.value);

		if (currentXValue > highestXValue) {
			highestXValue = currentXValue;
		}

		return {
			type: 'scatter',
			name: stat.country.name,
			color: '#10d4b6',
			data: [[currentXValue, Number(stat.annualRevenue?.value || 0)]],
		};
	});

	const roundingThreshold = highestXValue >= THOUSANDS_ROUNDING_THRESHOLD ? 1000 : 100;

	return (
		<div className='relative' style={{ minHeight: `${DEFAULT_HEIGHT}px` }}>
			<GenericChart
				invokeChartCallbackWhenCreated
				options={{
					chart: {
						type: 'scatter' as const,
						spacing: [10, 10, 0, 8],
						height: DEFAULT_HEIGHT,
						plotBackgroundColor: '#eafbf8',
					},

					colors: chartColors,

					series,
					plotOptions: {
						scatter: {
							marker: {
								symbol: 'circle',
								radius: 4,
								states: {
									hover: {
										enabled: true,
									},
								},
							},
							dataLabels: {
								enabled: true,
								verticalAlign: 'middle',
								align: 'left',
								formatter: function () {
									return this.series.name;
								},
								x: 10,
							},
							showInLegend: true,
						},
					},

					title: {
						text: '',
					},

					subtitle: {
						text: '',
					},

					xAxis: {
						crosshair: true,
						gridLineWidth: 1,
						gridLineColor: '#ccc',
						gridLineDashStyle: 'Dash',
						softMax: Math.ceil(highestXValue / roundingThreshold) * roundingThreshold,
						softMin: 0,
						title: {
							text: `Price [${currency}]`,
							margin: 10,
							style: {
								color: '#333',
								fontWeight: '600',
								fontSize: '12px',
								fontFamily,
							},
						},
					},
					yAxis: {
						crosshair: true,
						gridLineWidth: 1,
						gridLineColor: '#ccc',
						gridLineDashStyle: 'Dash',
						min: 0,
						softMax: 1000,
						title: {
							text: `Annual revenue [${currency}]`,
							margin: 0,
							x: -12,
							style: {
								color: '#333',
								fontWeight: '600',
								fontSize: '12px',
								fontFamily,
							},
						},
					},

					exporting: exportConfig,

					legend: {
						enabled: false,
					},

					tooltip: {
						shared: true,
						borderRadius: 0,
						borderColor: '#000',
						backgroundColor: 'rgba(255,255,255,1)',
						shadow: false,
						useHTML: true,
						padding: 0,
						headerFormat: '',

						formatter: function (this: Highcharts.TooltipFormatterContextObject): string {
							return `
							<div style="display:block;background: #fff;padding: 0.33rem; border: 0.06rem solid #000;">
							<strong style="display:block;margin-bottom:1px;font-size:12px;line-height:14px;color:${this.point.color};">${
								this.point.series.name
							}</strong>
							<span style="font-size:11px;line-height:14px;">Annual revenue: </span><strong style="font-size:11px;line-height:14px;">${roundedHumanFormat(
								currency,
								String(this.point.y)
							)}
							</strong>
							<br/>
              <span style="font-size:11px;line-height:14px;">Package price: </span><strong style="font-size:11px;line-height:14px;">${roundedHumanFormat(
								currency,
								String(this.point.x)
							)}</strong>
							</div>`;
						},
						style: {
							color: '#000',
							fontSize: '12px',
							fontFamily,
						},
					},
				}}
				onChartRendered={(scatter, _, { initialRender }) => {
					const plotleft = scatter.plotLeft,
						plottop = scatter.plotTop,
						plotwidth = scatter.plotWidth / 2,
						plotheight = scatter.plotHeight / 2;

					if (!initialRender) {
						// NOTE: in cases when chart rerenders we need to destroy the previous drawing
						renderedSvgRef.current?.destroy();
					}

					const isYGreaterThanZero =
						series.filter((x) => {
							if (x.type === 'scatter') {
								if ((x.data as number[][])[0][1] !== 0) return x;
							}
						}).length > 0;

					if (isYGreaterThanZero) {
						const nextSvg = scatter.renderer
							.rect(plotleft - 1, plotheight + plottop, plotwidth + 1, plotheight, 0)
							.attr({
								'stroke-width': 0.1,
								stroke: '#000',
								fill: '#000',
								opacity: '0.2',
								zIndex: 3,
							});

						nextSvg.add();
						renderedSvgRef.current = nextSvg;
					}
				}}
			/>
		</div>
	);
}
